/**
 * JTBD Problem Statement renderer (artifact_name: `jtbd_statement`).
 * JSON: { problem_statement: { fields: Field[4] }, deconstructing_components: Field[7] }.
 * The Clarity score + gaps are derived from the evidence tags (no score in the JSON).
 */

import React from 'react';
import { CardShell, LabelValueRow, ScoreHeader, SectionCard } from './shared/components';
import { gapsCount, weightedScore } from './shared/evidence';
import { fieldToJson, fieldToMarkdown, parseFields, type StructuredField } from './shared/field';
import { esc, htmlDoc, scoreHtml, tagSpanHtml } from './shared/html';
import { TagToggle } from './shared/TagToggle';
import type { ArtifactRenderer } from './types';

export interface JtbdModel {
  problem: StructuredField[]; // 4
  components: StructuredField[]; // 7
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';

// -- parse / serialize -------------------------------------------------------

export function parseJtbd(json: Record<string, unknown>): JtbdModel | null {
  const ps = json.problem_statement;
  const problem = isRecord(ps) ? parseFields(ps.fields) : [];
  const components = parseFields(json.deconstructing_components);
  // Require the JTBD anchor (the problem statement's "When"/"I want to" shape).
  if (problem.length < 3) return null;
  return { problem, components };
}

export function jtbdToJson(model: JtbdModel): Record<string, unknown> {
  return {
    problem_statement: { fields: model.problem.map(fieldToJson) },
    deconstructing_components: model.components.map(fieldToJson),
  };
}

const allFields = (m: JtbdModel) => [...m.problem, ...m.components];

const clarity = (m: JtbdModel) => {
  const fields = allFields(m);
  const score = weightedScore(fields);
  const gaps = gapsCount(fields);
  const summary =
    gaps > 0 ? `${gaps} gap${gaps === 1 ? '' : 's'} that need to be fulfilled` : 'Fully evidenced';
  return { score, summary };
};

export function jtbdToMarkdown(model: JtbdModel): string {
  const table = (title: string, fields: StructuredField[]) =>
    `## ${title}\n\n${fields.map(fieldToMarkdown).join('\n\n')}`;
  return [
    table('Problem Statement', model.problem),
    table('Deconstructing the components', model.components),
  ].join('\n\n');
}

// -- card --------------------------------------------------------------------

const JtbdCard: React.FC<{ model: JtbdModel }> = ({ model }) => {
  const { score, summary } = clarity(model);
  return (
    <CardShell testId="jtbd-artifact-card">
      <ScoreHeader score={score} label="Clarity score" tone="emerald" summary={summary} />
      <SectionCard title="JTBD Problem Statement">
        {model.problem.map((f) => (
          <LabelValueRow key={f.field} label={f.field} values={[f.value]} tag={f.tag} highlight />
        ))}
      </SectionCard>
      <SectionCard title="Deconstructing the components">
        {model.components.map((f) => (
          <LabelValueRow key={f.field} label={f.field} values={[f.value]} tag={f.tag} />
        ))}
      </SectionCard>
    </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const inputCls =
  'w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100';

const JtbdEditor: React.FC<{
  model: JtbdModel;
  onReady: (getJson: () => Record<string, unknown>) => void;
}> = ({ model, onReady }) => {
  const [state, setState] = React.useState<JtbdModel>(model);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  React.useEffect(() => {
    onReady(() => jtbdToJson(stateRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (section: 'problem' | 'components', i: number, p: Partial<StructuredField>) =>
    setState((prev) => ({
      ...prev,
      [section]: prev[section].map((f, j) => (j === i ? { ...f, ...p } : f)),
    }));

  const renderSection = (title: string, section: 'problem' | 'components') => (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </h3>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {state[section].map((f, i) => (
          <div key={f.field} className="py-2.5">
            <div className="mb-1.5 flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {f.field}
              </label>
              <TagToggle value={f.tag} onChange={(tag) => patch(section, i, { tag })} />
            </div>
            <textarea
              value={f.value}
              rows={2}
              onChange={(e) => patch(section, i, { value: e.target.value })}
              className={inputCls}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <CardShell>
      {renderSection('JTBD Problem Statement', 'problem')}
      {renderSection('Deconstructing the components', 'components')}
    </CardShell>
  );
};

// -- html --------------------------------------------------------------------

const rowHtml = (f: StructuredField, hl: boolean): string =>
  `<div class="row"><div class="label${hl ? ' hl' : ''}">${esc(f.field)} ${tagSpanHtml(f.tag)}</div>
   <div class="value"><p>${esc(f.value || '—')}</p></div></div>`;

export function jtbdToHtml(model: JtbdModel, title = 'JTBD Problem Statement'): string {
  const { score, summary } = clarity(model);
  const body = `${scoreHtml(score, 'Clarity score', 'emerald', summary)}
    <section class="card"><h3>JTBD Problem Statement</h3>${model.problem.map((f) => rowHtml(f, true)).join('')}</section>
    <section class="card"><h3>Deconstructing the components</h3>${model.components.map((f) => rowHtml(f, false)).join('')}</section>`;
  return htmlDoc(title, body);
}

export const jtbdRenderer: ArtifactRenderer<JtbdModel> = {
  type: 'jtbd_statement',
  matchNames: ['jtbd_statement', 'problem_statement', 'jtbd'],
  parse: parseJtbd,
  toJson: jtbdToJson,
  toMarkdown: jtbdToMarkdown,
  toHtml: jtbdToHtml,
  Card: JtbdCard,
  Editor: JtbdEditor,
};
